import { Auth } from 'aws-amplify';

import {
  broadcastSignOut,
  clearIdleOverride,
  clearLastActivity,
  INACTIVITY_REASON,
  SIGNOUT_REASON_KEY,
} from './sessionTimeout';

/**
 * The single sign-out path, shared by the Signout menu item (Navigation.tsx)
 * and the inactivity timeout (inactivity-watcher.tsx).
 *
 * The reload is not incidental — it IS the store reset. Every MobX store is a
 * singleton built once in index.tsx, several of them hold PHI
 * (userProfileUIStore.currentUserProfile, clientEntryStore.clients) and two
 * memoize multi-MB reference payloads for the whole session. There is no
 * programmatic teardown, so reloading is what guarantees the next practitioner
 * at that browser starts clean.
 */
export async function signOutAndReload(reason?: string): Promise<void> {
  if (reason) {
    try {
      // sessionStorage, not localStorage: the reason is for THIS tab's sign-in
      // screen, and it must not linger for the next session. It survives the
      // reload below, which is all it needs to do. Other tabs share the same
      // activity clock, so they reach the same conclusion and stamp their own.
      sessionStorage.setItem(SIGNOUT_REASON_KEY, reason);
    } catch (err) {
      // Losing the explanatory banner is cosmetic; never block the sign-out.
    }
  }

  clearLastActivity();
  // Tell every other window before the tokens go. The PDF popup holds a fully
  // rendered client report in its own sessionStorage and would otherwise keep
  // displaying it indefinitely on an unattended machine, which is precisely
  // what this feature exists to prevent. A second normal tab has the same
  // problem, just less starkly.
  broadcastSignOut(Date.now());
  // One `?idleMinutes=` load buys one test cycle. Keeping it would re-arm the
  // short limit on the next sign-in and kick the developer out again.
  clearIdleOverride();

  try {
    await Auth.signOut();
  } catch (err) {
    // A failed signOut still has to end in a reload. Leaving a practitioner
    // sitting in an authenticated UI because the network blipped is the one
    // outcome this function exists to prevent.
    console.error('Auth.signOut() failed; reloading anyway.', err);
  } finally {
    window.location.reload();
  }
}

export function signOutForInactivity(): Promise<void> {
  return signOutAndReload(INACTIVITY_REASON);
}

/**
 * Reads and consumes the reason stamped above. Consuming on read is what keeps
 * the banner to a single appearance — the Amplify authenticator re-renders the
 * sign-in screen on every keystroke and state change.
 */
export function consumeSignOutReason(): string | undefined {
  try {
    const reason = sessionStorage.getItem(SIGNOUT_REASON_KEY);
    if (reason) sessionStorage.removeItem(SIGNOUT_REASON_KEY);
    return reason || undefined;
  } catch (err) {
    return undefined;
  }
}
